import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router';
import { track } from '../../analytics/track';
import { AnalyticsEvents } from '../../analytics/events';

declare global {
  interface Window {
    zE?: (type: 'messenger', command: string, action?: (callback: (token: string) => void) => void) => void;
  }
}

let messenger = (command: string, action?: (callback: (token: string) => void) => void) => {
  console.warn('ZendeskSDK not initialized', { command, action });
};

export class ZendeskMessengerApi {
  static hide() {
    messenger('hide');
  }

  static show() {
    messenger('show');
  }

  static async login(token: string) {
    messenger('loginUser', (callback) => {
      callback(token);
    });
  }

  static logout() {
    messenger('logoutUser');
  }
}

const useZendeskWidget = (
  enabled: boolean,
  zendeskKey?: string,
  excludedPaths: string[] = [],
  unloadOnDisable = true,
) => {
  const widgetRef = useRef<HTMLElement | undefined>(undefined);
  const { pathname } = useLocation();

  useEffect(() => {
    const normalizedPath = pathname.replace(/\/+$/, '');
    const shouldLoad = enabled && zendeskKey && !excludedPaths.includes(normalizedPath);

    const load = () => {
      if (widgetRef.current || document.getElementById('ze-snippet')) return;

      const script = document.createElement('script');
      script.src = `https://static.zdassets.com/ekr/snippet.js?key=${zendeskKey}`;
      script.id = 'ze-snippet';
      script.async = true;
      script.onload = () => {
        messenger = (...args) => {
          if (window.zE) window.zE('messenger', ...args);
        };
        // Record when a recruiter opens the support chat (Zendesk messaging "open" event).
        const zE = window.zE as unknown as ((...args: unknown[]) => void) | undefined;
        zE?.('messenger:on', 'open', () => track(AnalyticsEvents.SUPPORT_CHAT_OPENED));
      };
      widgetRef.current = script;
      document.body.appendChild(script);
    };

    if (shouldLoad) {
      load();
      ZendeskMessengerApi.show();
    } else {
      ZendeskMessengerApi.hide();
    }
  }, [enabled, unloadOnDisable, zendeskKey, excludedPaths, pathname]);
};

export default useZendeskWidget;
